import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import { postNotification } from "../../components/utilities/Notifications";
import Comments from "../../components/utilities/Comments";
import { cardUtility } from "./CardUtil";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { CardText } from "./components/CardCard";
import { getData, postData } from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

const interleave = (arr, thing) =>
  [].concat(...arr.map((n) => [n, thing])).slice(0, -1);

const replaceWithIcons = (textBox) => {
  var replaceItems = {};
  let text = textBox?.replace(/\\/g, "") || "";
  let replaceList = [
    { key: "M", fileName: "mars" },
    { key: "L", fileName: "luna" },
    { key: "S", fileName: "sol" },
    { key: "0", fileName: "0" },
    { key: "1", fileName: "1" },
    { key: "2", fileName: "2" },
    { key: "3", fileName: "3" },
    { key: "4", fileName: "4" },
    { key: "5", fileName: "5" },
    { key: "6", fileName: "6" },
    { key: "7", fileName: "7" },
    { key: "X", fileName: "x" },
  ];

  for (let rep of replaceList) {
    if (text.includes(`${rep.key}`)) {
      replaceItems[`${rep.key}`] = (
        <img
          src={`${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/images/assets/${rep.fileName}.png`}
          alt={rep.label || rep.key}
          style={{
            height: "18px",
            marginBottom: "-4px",
          }}
        />
      );
    }
  }

  if (!Object.keys(replaceItems).length) {
    return text;
  }
  var newTextArray = [text];

  Object.keys(replaceItems).forEach((replaceItem) => {
    // loop through each replaceItem
    for (var i = 0; i < newTextArray.length; i++) {
      // loop through each already split item
      if (typeof newTextArray[i] === "string") {
        var item = newTextArray[i].split(replaceItem);
        if (item.length > 1) {
          item = interleave(item, replaceItems[replaceItem]);
          newTextArray.splice(i, 1, ...item);
        }
      }
    }
  });
  return newTextArray;
};

const fetchFile = async (fileId) => {
  if (!fileId) return;

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {
        size: "large",
      },
    });
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const ImagePreview = (props) => {
  const { card } = props;
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      if (!card.imageFileId) {
        setImage({
          url: `${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/images/assets/cardback.png`,
        });
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === card.imageFileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = await fetchFile(card.imageFileId);
        if (file) {
          setImage({
            id: card.imageFileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [card]);

  if (!image?.url || loading) {
    return (
      <Box
        sx={{
          flex: "0 0 1",
          maxWidth: "100%",
          minWidth: "100%",
          mt: "-5px !important",
          borderRadius: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {loading && <CircularProgress size={50} />}
      </Box>
    );
  }
  return (
    <img
      src={image.url}
      alt={card.name}
      style={{
        flex: "0 0 1",
        maxWidth: "100%",
        minWidth: "100%",
        mt: "-5px !important",
        borderRadius: 2,
      }}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  label: {
    fontWeight: "bold",
    fontSize: "0.80em",
  },
  nonLabel: {
    fontSize: "0.85em",
  },
}));

export default function Card() {
  const classes = useStyles();
  const cardUtil = cardUtility();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  let { cardId } = useParams();

  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const go = async () => {
      try {
        var apiCard = await getData(`/cards/${cardId}`, {
          queryStringParameters: {},
        });
        apiCard.dustCostStylized = replaceWithIcons(apiCard.dustCost || "");

        setCard(apiCard);
      } catch (err) {
        postNotification("error", err);
        navigate("/home");
        setLoading(false);
        return;
      }
      setLoading(false);

      try {
        var apiAnalytics = await postData(`/cards/${cardId}/analytics`);
        setAnalytics(apiAnalytics);
      } catch (err) {
        setAnalytics(null);
      }
    };

    go();
    // eslint-disable-next-line
  }, [cardId]);

  return (
    <>
      {!loading && card && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid container sx={{ gap: 2 }}>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={4}>
                  <ImagePreview card={card} />
                </Grid>

                <Grid item xs={12} md={8} sx={{ mt: [null, 3] }}>
                  {card.banned && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      This card is not tournament legal
                    </Alert>
                  )}
                  <Grid container sx={{ mb: 2 }}>
                    <Grid item xs={4} sm={4}>
                      <Typography className={classes.label} gutterBottom>
                        Card Name
                      </Typography>
                    </Grid>
                    <Grid item xs={8} sm={8}>
                      <Typography className={classes.nonLabel} gutterBottom>
                        {card.name}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container sx={{ mb: 2 }}>
                    {card.dustCostStylized?.length > 0 && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Dust Cost
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.dustCostStylized}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {card.devotion !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Devotion
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.devotion}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {card.cardType !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Card Type
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.cardType}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {card.cardSubType !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            {cardUtil.cardSubTypes?.[card.cardType]
                              ?.cardSubTypeLabel || "Sub Type"}
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.cardSubType}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {card.cardSubTypeChild !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            {
                              cardUtil.cardSubTypes?.[card.cardType]
                                ?.cardSubTypeChildLabel
                            }
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.cardSubTypeChild}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Grid container sx={{ mb: 2 }}>
                    {card.attack !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Attack
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.attack}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {card.defence !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Defence
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.defence}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {card.trait !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Trait
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.trait}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {card.resist !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Resistance
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.resist}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  <Grid container sx={{ mb: 2 }}>
                    {card.cardSet !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Set
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.cardSet}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {card.serial !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Serial
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.serial}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {card.cardNumber !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Card Number
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.cardNumber}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {card.rarity !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Rarity
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.rarity}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Grid container sx={{ mb: 2 }}>
                    {card.flavorText !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            flavorText
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.flavorText}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {card.illustrator !== undefined && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.label} gutterBottom>
                            Illustrator
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={8}>
                          <Typography className={classes.nonLabel} gutterBottom>
                            {card.illustrator}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {(authContext?.authState?.identity?.isAdmin === true ||
                    (card.gameSystemId &&
                      authContext?.authState?.identity?.adminGameSystems?.includes(
                        card.gameSystemId
                      ))) && (
                    <Grid container>
                      <Grid item xs={4} sm={4}>
                        <Typography className={classes.label} gutterBottom>
                          Views
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sm={8}>
                        <Typography className={classes.nonLabel} gutterBottom>
                          {analytics?.views || 0}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {(card.keywords?.length ||
              (card.textBox && card.textBox !== "")) && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              >
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={12} textAlign="center">
                    <Typography variant="h3" gutterBottom paragraph>
                      Card Text
                    </Typography>
                  </Grid>

                  {card.keywords?.length > 0 && (
                    <Grid item xs={12} textAlign="center">
                      <Typography variant="h5" gutterBottom paragraph>
                        {card.keywords.join(" - ")}
                      </Typography>
                    </Grid>
                  )}
                  {card?.banned && (
                    <Grid item xs={12}>
                      <Typography
                        variant="h5"
                        color="error"
                        gutterBottom
                        paragraph
                      >
                        Note: Card banned
                      </Typography>
                    </Grid>
                  )}
                  {card.textBox && card.textBox !== "" && (
                    <Grid item xs={12}>
                      <CardText card={card} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            {(authContext?.authState?.identity?.isAdmin === true ||
              (card.gameSystemId &&
                authContext?.authState?.identity?.adminGameSystems?.includes(
                  card.gameSystemId
                ))) && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 1,
                }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  href={`/admin/card/${card.id}`}
                >
                  Edit
                </Button>
              </Grid>
            )}

            <Comments cardId={cardId} gameSystemId={card?.gameSystemId} />
          </Grid>
        </Container>
      )}
      {loading && <ModalLoadingFull />}
    </>
  );
}
